import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Zap, FileSpreadsheet } from "lucide-react";
import { cn } from "@/lib/utils";

// Searchable, scrollable dataset picker (used as the per-widget primary source selector).
export function SourcePicker({ value, datasets, onChange, disabled = false, testid = "widget-source-select", placeholder = "Pick a dataset…", className }) {
  const [open, setOpen] = useState(false);
  const current = useMemo(() => (datasets || []).find((d) => d.id === value), [datasets, value]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} disabled={disabled} aria-label="Data source"
          className={cn("flex items-center gap-1.5 h-8 px-2 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] text-xs text-left min-w-0 flex-1 disabled:opacity-50", className)}>
          {current ? (current.mode === "live" ? <Zap className="w-3 h-3 shrink-0 text-[color:var(--brand-mint)]" /> : <FileSpreadsheet className="w-3 h-3 shrink-0 text-[color:var(--brand-ocean)]" />) : null}
          <span className={cn("truncate flex-1", !current && "text-[color:var(--ink-3)]")} data-testid={`${testid}-value`}>{current?.name || (value ? "(missing dataset)" : placeholder)}</span>
          <ChevronsUpDown className="w-3 h-3 shrink-0 text-[color:var(--ink-3)]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput data-testid={`${testid}-search`} placeholder="Search datasets…" className="h-8 text-xs" />
          <CommandList className="max-h-64">
            <CommandEmpty className="text-xs p-3 text-center text-[color:var(--ink-3)]">No dataset matches</CommandEmpty>
            <CommandGroup>
              {(datasets || []).map((d) => (
                <CommandItem key={d.id} value={`${d.name} ${d.id}`} data-testid={`${testid}-option`} data-dataset-id={d.id}
                  onSelect={() => { setOpen(false); if (d.id !== value) onChange(d.id); }} className="text-xs gap-2">
                  {d.mode === "live" ? <Zap className="w-3 h-3 shrink-0 text-[color:var(--brand-mint)]" /> : <FileSpreadsheet className="w-3 h-3 shrink-0 text-[color:var(--brand-ocean)]" />}
                  <span className="truncate flex-1">{d.name}</span>
                  <span className="text-[10px] text-[color:var(--ink-3)]">{d.mode === "live" ? "live" : (d.row_count?.toLocaleString?.() || "")}</span>
                  {d.id === value && <Check className="w-3 h-3 text-[color:var(--brand-ocean)]" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
